import { useEffect, useRef } from "react";
import { useRoomContext, useConnectionState } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { useLiveKitSession } from "./LiveKitSessionContext";
import { ENDPOINTS } from "./endpoints";

/**
 * Inside LiveKitRoom: exchanges LiveKit access token for telemetry JWT (timeline + client logs).
 *
 * Important: do not use `room.state` alone in React effect deps — Room is mutable and React will
 * not re-render when it flips to connected. useConnectionState(room) subscribes to updates.
 */
export default function TelemetryBootstrap() {
  const room = useRoomContext();
  const connectionState = useConnectionState(room);
  const { accessToken, setTelemetryJwt, setIntegrityJwt } = useLiveKitSession();
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!accessToken || connectionState !== ConnectionState.Connected || !room?.name) {
      setTelemetryJwt(null);
      setIntegrityJwt(null);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const res = await fetch(ENDPOINTS.TELEMETRY_TOKEN, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ roomName: room.name }),
        });
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.warn(
            "[LiveKit] TELEMETRY_TOKEN failed",
            res.status,
            errText?.slice(0, 200),
            "→ check REACT_APP_TOKEN_API_URL, server /livekit/telemetry/token, LIVEKIT_API_SECRET / TELEMETRY_JWT_SECRET, CORS"
          );
          if (!cancelled) {
            setTelemetryJwt(null);
            setIntegrityJwt(null);
          }
          return;
        }
        const data = await res.json();
        if (!cancelled && data.telemetryToken) {
          setTelemetryJwt(data.telemetryToken);
          // TI-735: integrity token is best-effort (null when integrity-service isn't wired in
          // this env) — the monitor stays idle without it rather than sending a wrong token.
          setIntegrityJwt(data.integrityToken ?? null);
          if (process.env.NODE_ENV === "development") {
            console.log("[LiveKit] telemetry JWT refreshed (timeline + client logs enabled)");
          }
        }
      } catch (e) {
        console.warn("[LiveKit] TELEMETRY_TOKEN network error:", e?.message || e);
        if (!cancelled) {
          setTelemetryJwt(null);
          setIntegrityJwt(null);
        }
      }
    };

    run();
    intervalRef.current = window.setInterval(run, 50 * 60 * 1000);

    return () => {
      cancelled = true;
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      setTelemetryJwt(null);
      setIntegrityJwt(null);
    };
  }, [accessToken, connectionState, room?.name, setTelemetryJwt, setIntegrityJwt]);

  return null;
}
